import {useCallback, useEffect, useRef, type MutableRefObject} from 'react';
import {clampTablePosition, type TablePosition} from 'app/components/pages/poker/proom/proomWidgetGeometry';

interface DragHandlers {
    blur: (() => void) | null
    end: ((e: PointerEvent) => void) | null
    lostCapture: (() => void) | null
    move: ((e: PointerEvent) => void) | null
}

interface UseProomTableWindowDragParams {
    boardId: string
    desktopPosition: TablePosition
    isMobile: boolean
    onPositionChange: (boardId: string, position: TablePosition) => void
    width: number
}

interface DragRefs {
    activePointerId: MutableRefObject<number | null>
    dragHandleRef: MutableRefObject<HTMLDivElement | null>
    dragHandlersRef: MutableRefObject<DragHandlers>
    dragOffset: MutableRefObject<TablePosition>
}

interface AttachDragListenersParams {
    boardId: string
    cleanupDrag: () => void
    handleElement: HTMLDivElement
    onPositionChange: (boardId: string, position: TablePosition) => void
    refs: DragRefs
    width: number
}

const POINTER_CAPTURE_OPTIONS: AddEventListenerOptions = {capture: true};

const detachDragListeners = (refs: DragRefs): void => {
    if (refs.dragHandleRef.current && refs.dragHandlersRef.current.lostCapture) {
        refs.dragHandleRef.current.removeEventListener(
            'lostpointercapture',
            refs.dragHandlersRef.current.lostCapture
        );
        refs.dragHandlersRef.current.lostCapture = null;
    }
    refs.dragHandleRef.current = null;

    if (refs.dragHandlersRef.current.move) {
        window.removeEventListener('pointermove', refs.dragHandlersRef.current.move, POINTER_CAPTURE_OPTIONS);
        refs.dragHandlersRef.current.move = null;
    }
    if (refs.dragHandlersRef.current.end) {
        window.removeEventListener('pointerup', refs.dragHandlersRef.current.end, POINTER_CAPTURE_OPTIONS);
        window.removeEventListener('pointercancel', refs.dragHandlersRef.current.end, POINTER_CAPTURE_OPTIONS);
        refs.dragHandlersRef.current.end = null;
    }
    if (refs.dragHandlersRef.current.blur) {
        window.removeEventListener('blur', refs.dragHandlersRef.current.blur);
        refs.dragHandlersRef.current.blur = null;
    }
};

const attachDragListeners = ({
    boardId,
    cleanupDrag,
    handleElement,
    onPositionChange,
    refs,
    width
}: AttachDragListenersParams): void => {
    const moveHandler = (ev: PointerEvent): void => {
        if (refs.activePointerId.current !== ev.pointerId) {
            return;
        }
        onPositionChange(boardId, clampTablePosition({
            x: ev.clientX - refs.dragOffset.current.x,
            y: ev.clientY - refs.dragOffset.current.y
        }, width));
    };

    const endHandler = (ev: PointerEvent): void => {
        if (refs.activePointerId.current !== null && ev.pointerId !== refs.activePointerId.current) {
            return;
        }
        if (handleElement.hasPointerCapture(ev.pointerId)) {
            handleElement.releasePointerCapture(ev.pointerId);
        }
        cleanupDrag();
    };

    refs.dragHandlersRef.current.move = moveHandler;
    refs.dragHandlersRef.current.end = endHandler;
    refs.dragHandlersRef.current.blur = cleanupDrag;
    refs.dragHandlersRef.current.lostCapture = cleanupDrag;
    handleElement.addEventListener('lostpointercapture', cleanupDrag);
    window.addEventListener('pointermove', moveHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('pointerup', endHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('pointercancel', endHandler, POINTER_CAPTURE_OPTIONS);
    window.addEventListener('blur', cleanupDrag);
};

const isIgnoredDragStart = (
    event: React.PointerEvent<HTMLDivElement>,
    isMobile: boolean
): boolean => isMobile ||
    event.button !== 0 ||
    Boolean((event.target as HTMLElement).closest('[data-no-drag="true"]'));

export const useProomTableWindowDrag = ({
    boardId,
    desktopPosition,
    isMobile,
    onPositionChange,
    width
}: UseProomTableWindowDragParams): ((event: React.PointerEvent<HTMLDivElement>) => void) => {
    const dragRefs: DragRefs = {
        activePointerId: useRef<number | null>(null),
        dragHandleRef: useRef<HTMLDivElement | null>(null),
        dragHandlersRef: useRef<DragHandlers>({blur: null, end: null, lostCapture: null, move: null}),
        dragOffset: useRef<TablePosition>({x: 0, y: 0})
    };

    const cleanupDrag = useCallback(() => {
        dragRefs.activePointerId.current = null;
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        detachDragListeners(dragRefs);
    }, []);

    const handleDragStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (isIgnoredDragStart(event, isMobile)) {
            return;
        }

        cleanupDrag();

        const handleElement = event.currentTarget;
        dragRefs.dragHandleRef.current = handleElement;
        dragRefs.dragOffset.current = {
            x: event.clientX - desktopPosition.x,
            y: event.clientY - desktopPosition.y
        };
        dragRefs.activePointerId.current = event.pointerId;

        handleElement.setPointerCapture(event.pointerId);
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        attachDragListeners({
            boardId,
            cleanupDrag,
            handleElement,
            onPositionChange,
            refs: dragRefs,
            width
        });
        event.preventDefault();
        event.stopPropagation();
    }, [
        boardId,
        cleanupDrag,
        desktopPosition,
        isMobile,
        onPositionChange,
        width
    ]);

    useEffect(() => () => cleanupDrag(), [cleanupDrag]);

    return handleDragStart;
};
